import React, { useEffect, useState } from 'react';
import { DropletsIcon, HistoryIcon, RepeatIcon } from 'lucide-react';
const historicalYears = ['2024', '2025', '2026'];
const historicalUnits = ['150', '250', '1,200'];
const planYears = ['2027', '2028', '2029', '2030', '2031'];
const planUnits = ['1,500', '3,000', '6,000', '9,000', '12,000'];
type ModelRow = {
  id: string;
  label: string;
  historical: string[];
  values: string[];
  sub?: string;
  historicalSub?: string[];
  subValues?: string[];
  emphasis?: 'consumables' | 'revenue' | 'cost' | 'ebitda';
};
const revenueRows: ModelRow[] = [{
  id: 'equip-direct',
  label: 'Equipment — direct',
  historical: ['1.01', '1.20', '5.80'],
  values: ['1.32', '4.43', '11.86', '24.58', '43.70']
}, {
  id: 'equip-partners',
  label: 'Equipment — partners',
  historical: ['—', '—', '2.86'],
  values: ['6.45', '12.63', '24.16', '34.44', '43.40']
}, {
  id: 'consumables',
  label: 'Consumables revenue',
  historical: ['1.32', '2.30', '7.02'],
  values: ['20.01', '43.67', '94.63', '152.89', '218.44'],
  sub: 'Share of total revenue',
  historicalSub: ['57%', '66%', '45%'],
  subValues: ['72%', '72%', '72%', '72%', '72%'],
  emphasis: 'consumables'
}];
const summaryRows: ModelRow[] = [{
  id: 'total-revenue',
  label: 'Total revenue',
  historical: ['2.33', '3.50', '15.68'],
  values: ['27.78', '60.73', '130.66', '211.91', '305.54'],
  emphasis: 'revenue'
}, {
  id: 'cogs',
  label: 'Total COGS',
  historical: ['1.55', '2.50', '9.41'],
  values: ['12.04', '24.45', '51.00', '79.64', '110.37'],
  emphasis: 'cost'
}, {
  id: 'gross-profit',
  label: 'Gross profit',
  historical: ['0.78', '0.90', '6.27'],
  values: ['15.75', '36.27', '79.66', '132.27', '195.18'],
  sub: 'Gross margin',
  historicalSub: ['34%', '26%', '40%'],
  subValues: ['57%', '60%', '61%', '62%', '64%']
}, {
  id: 'opex',
  label: 'Opex',
  historical: ['2.74', '1.46', '3.38'],
  values: ['11.11', '15.18', '32.66', '52.98', '76.39'],
  emphasis: 'cost'
}, {
  id: 'ebitda',
  label: 'EBITDA',
  historical: ['-1.69', '-1.30', '2.89'],
  values: ['4.63', '21.09', '46.99', '79.30', '118.79'],
  sub: 'EBITDA margin',
  historicalSub: ['NA', 'NA', '18%'],
  subValues: ['17%', '35%', '36%', '37%', '39%'],
  emphasis: 'ebitda'
}];
const planMix = {
  share: [72, 72, 72, 72, 72],
  revenue: [27.78, 60.73, 130.66, 211.91, 305.54]
};
const historicalMix = {
  share: [57, 66, 45],
  revenue: [2.33, 3.5, 15.68]
};
export function FinancialHighlightsVisual() {
  const [pulse, setPulse] = useState(0);
  const [showHistoricals, setShowHistoricals] = useState(false);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const interval = window.setInterval(() => setPulse(current => (current + 1) % 4), 1100);
    return () => window.clearInterval(interval);
  }, []);
  const years = showHistoricals ? historicalYears : planYears;
  const units = showHistoricals ? historicalUnits : planUnits;
  const mix = showHistoricals ? historicalMix : planMix;
  const maxRevenue = Math.max(...mix.revenue);
  return <section className="grid min-h-0 min-w-0 flex-1 gap-3 lg:grid-cols-[.66fr_1.34fr] lg:gap-4" aria-label="Financial model and the role of consumables revenue" data-id="element-975" data-mp="37y8pfrp6e1lk">
      <div className="flex min-h-0 min-w-0 flex-col gap-2" data-id="element-976" data-mp="33075l4jxdy91">
        <article className="relative flex flex-1 flex-col justify-center overflow-hidden rounded-2xl border border-blue-300/40 bg-blue-400/10 p-3" data-id="element-977" data-mp="2faw3njqu9xtb">
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-300" data-id="element-978" data-mp="2balyuaes9dg1">The consumables engine</p>
          <p className="mt-1.5 text-sm font-bold leading-snug text-white" data-id="element-979" data-mp="2k8xnw800436d">
            The machine is sold once. The pour is sold every day.
          </p>

          <div className="mt-2.5 flex items-center gap-2" aria-hidden="true" data-id="element-980" data-mp="4g8dy2keve37">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/10 text-blue-100" data-id="element-981" data-mp="3vn1f56f7fd7q">
              <RepeatIcon className="h-4 w-4" data-id="element-982" data-mp="258jzf2e7s06o" />
            </span>
            <span className="relative h-[3px] flex-1 overflow-hidden rounded-full bg-white/15" data-id="element-983" data-mp="2a5k9doe6pn32">
              <span className="pd3-fin-flow absolute inset-y-0 left-0 w-1/3 rounded-full bg-blue-400" data-id="element-984" data-mp="2f4ncv4einl8x" />
            </span>
            <span className="flex gap-1" data-id="element-985" data-mp="2j7jouc9jwj3w">
              {[0, 1, 2, 3].map(index => <span key={index} className={`flex h-6 w-6 items-center justify-center rounded-lg transition-colors duration-300 ${index <= pulse ? 'bg-blue-400 text-navy-950' : 'bg-white/10 text-blue-200'}`} data-id="element-986" data-mp="2o4tn8hex453v">
                  <DropletsIcon className="h-3 w-3" data-id="element-987" data-mp="32ure5548f8wr" />
                </span>)}
            </span>
          </div>

          <p className="mt-2.5 text-[11px] leading-relaxed text-slate-300" data-id="element-988" data-mp="36x49czlcrofa">
            Each installed JuLi™ converts a one-time equipment sale into a daily consumables annuity — and every unit shipped compounds onto the base already pouring.
          </p>
        </article>

        <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-1" data-id="element-989" data-mp="v8zhzpzckj8x">
          {[['72%', 'of five-year revenue is consumables', '$530M of $737M cumulative'], ['86%', 'of 2027 gross profit is consumables', 'Equipment carries volume; consumables carry margin'], ['+22 pts', 'EBITDA margin expansion by 2031', '17% → 39% as the installed base compounds']].map(([stat, label, detail]) => <div key={stat} className="rounded-xl border border-white/15 bg-white/5 px-3 py-2" data-id="element-990" data-mp="1ey1gx0zuneqk">
              <p className="text-lg font-bold leading-none tabular-nums text-white" data-id="element-991" data-mp="1a58cyfc7gafj">{stat}</p>
              <p className="mt-1 text-[11px] font-bold leading-tight text-blue-100" data-id="element-992" data-mp="157ymcwd2xsq4">{label}</p>
              <p className="mt-0.5 text-[10px] leading-snug text-slate-400" data-id="element-993" data-mp="22mt0x53acqs9">{detail}</p>
            </div>)}
        </div>
      </div>

      <div className="flex min-h-0 min-w-0 flex-col rounded-2xl border border-white/15 bg-white/5 p-3 sm:p-3.5" data-id="element-994" data-mp="1xujdbvpn82t5">
        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-995" data-mp="2gufkaychtkyu">
          <div className="min-w-0" data-id="element-996" data-mp="2lqeex3oezz7p">
            <p className="text-sm font-bold text-white" data-id="element-997" data-mp="27zrixja1gnfb">
              {showHistoricals ? 'Historical performance' : 'Five-year plan'}{' '}
              <span className="font-semibold text-slate-300" data-id="element-998" data-mp="2c01x3j7zik59">· USD Mn</span>
            </p>
            <p className="mt-0.5 text-[10px] font-semibold text-blue-200" data-id="element-999" data-mp="1y0k2gn80ccnz">
              {showHistoricals ? 'Organic growth · actuals through 2026' : 'Forward plan · 2027 – 2031'}
            </p>
          </div>
          <button type="button" onClick={() => setShowHistoricals(current => !current)} aria-pressed={showHistoricals} className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${showHistoricals ? 'border-blue-300 bg-blue-400 text-navy-950' : 'border-white/20 bg-navy-950/50 text-blue-100 hover:border-blue-300/60'}`} data-id="element-1000" data-mp="1p28dsqqpboy6">
            <HistoryIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1001" data-mp="17tfmpv67y2fu" />
            {showHistoricals ? 'Show 2027–2031' : 'Show 2024–2026'}
          </button>
        </div>

        <div className="mt-2 min-h-0 flex-1" data-id="element-1002" data-mp="1gr818x449626">
          <table className="w-full border-collapse text-left" data-id="element-1003" data-mp="1gqg6tpm99edh">
            <caption className="sr-only" data-id="element-1004" data-mp="1vfuemta6nnqr">
              {showHistoricals ? 'Historical financial performance in USD millions from 2024 to 2026' : 'Five-year financial plan in USD millions from 2027 to 2031'}
            </caption>
            <thead data-id="element-1005" data-mp="1zi8zfixewxko">
              <tr className="border-b border-white/20" data-id="element-1006" data-mp="1zjttl7fteei0">
                <th scope="col" className="w-[26%] px-2 py-1 text-left text-[10px] font-bold uppercase tracking-[.14em] text-slate-400" data-id="element-1007" data-mp="a3uezatffwuz">JuLi™ units sold</th>
                {years.map((year, index) => <th key={year} scope="col" className="px-2 py-1 text-right" data-id="element-1008" data-mp="3geft5lydbd2e" data-mpi={index}>
                    <span className={`block text-[11px] font-bold ${showHistoricals ? 'text-slate-200' : 'text-blue-100'}`} data-id="element-1009" data-mp="3gcd5v9olny1d">{year}</span>
                    <span className="block text-[10px] font-semibold tabular-nums text-slate-400" data-id="element-1010" data-mp="3p69fxq4hpk0f">{units[index]}</span>
                  </th>)}
              </tr>
            </thead>
            <tbody key={showHistoricals ? 'historical' : 'plan'} className="[animation:pd3-rise-in_.4s_ease-out]" data-id="element-1011" data-mp="2xg3itwvpzx58">
              {revenueRows.map(row => <FinancialModelRow key={row.id} row={row} showHistoricals={showHistoricals} years={years} data-id="element-1012" data-mp="31j9n4yz9ve92" />)}
              <tr aria-hidden="true" data-id="element-1013" data-mp="3bfcwopwgef61"><td colSpan={years.length + 1} className="h-1" data-id="element-1014" data-mp="3bfmrqrwcvcil" /></tr>
              {summaryRows.map(row => <FinancialModelRow key={row.id} row={row} showHistoricals={showHistoricals} years={years} data-id="element-1015" data-mp="lozm5dwktocs" />)}
            </tbody>
          </table>
        </div>

        <div className="mt-2 border-t border-white/15 pt-2" data-id="element-1016" data-mp="2b6wpy108bwxb">
          <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-1017" data-mp="2b9974by5y39b">
            <p className="text-[10px] font-bold uppercase tracking-[.14em] text-blue-300" data-id="element-1018" data-mp="268ljm09584ju">Revenue mix — consumables vs equipment</p>
            <div className="flex items-center gap-3 text-[9px] font-bold uppercase tracking-[.1em]" data-id="element-1019" data-mp="21f0ja1k2uyhh">
              <span className="flex items-center gap-1.5 text-blue-200" data-id="element-1020" data-mp="1wgfpajvf5b1h"><span className="h-2 w-4 rounded-full bg-blue-400" aria-hidden="true" data-id="element-1021" data-mp="1shgdqgf6mg12" />Consumables</span>
              <span className="flex items-center gap-1.5 text-slate-400" data-id="element-1022" data-mp="1ng0t2pgx2bj7"><span className="h-2 w-4 rounded-full bg-white/25" aria-hidden="true" data-id="element-1023" data-mp="1ihzae5neke89" />Equipment</span>
            </div>
          </div>
          <div className={`mt-1.5 grid gap-1.5 ${showHistoricals ? 'grid-cols-3' : 'grid-cols-5'}`} aria-hidden="true" data-id="element-1024" data-mp="1dkpmwvjkqcn2">
            {years.map((year, index) => <div key={year} className="flex flex-col items-center gap-0.5" data-id="element-1025" data-mp="37wxebywosbe3" data-mpi={index}>
                <span className="flex h-9 w-full items-end" data-id="element-1026" data-mp="3w0kjvb94k12k">
                  <span className="pd3-fin-column relative flex w-full flex-col-reverse overflow-hidden rounded-t-md bg-white/15" style={{
                ['--pd3-fin-h' as string]: `${Math.max(14, mix.revenue[index] / maxRevenue * 100)}%`,
                animationDelay: `${index * 140}ms`
              }} data-id="element-1027" data-mp="3vyzqvq2gkq6t">
                    <span className="w-full bg-blue-400" style={{
                  height: `${mix.share[index]}%`
                }} data-id="element-1028" data-mp="4sf2yf7p9ye0" />
                  </span>
                </span>
                <span className="text-[9px] font-bold tabular-nums text-slate-400" data-id="element-1029" data-mp="38c1bywtzlqb0">{year}</span>
              </div>)}
          </div>
        </div>

        <p className="mt-1.5 text-right text-[9px] leading-relaxed text-slate-400" data-id="element-1030" data-mp="3n1pbbsilyew3">
          Source: Company financials and management business plan. 2024–2026 reflect the historical organic base; 2027–2031 are illustrative planning information only, not a forecast or assurance of outcome.
        </p>
      </div>
    </section>;
}
function FinancialModelRow({
  row,
  showHistoricals,
  years
}: {
  row: ModelRow;
  showHistoricals: boolean;
  years: string[];
}) {
  const tone = row.emphasis === 'consumables' ? 'bg-blue-400/18' : row.emphasis === 'revenue' ? 'bg-blue-400/12' : row.emphasis === 'ebitda' ? 'bg-blue-500/20' : row.emphasis === 'cost' ? 'bg-navy-950/45' : '';
  const isKey = row.emphasis === 'revenue' || row.emphasis === 'ebitda' || row.emphasis === 'consumables';
  const valueTone = row.emphasis === 'ebitda' || row.emphasis === 'consumables' ? 'text-blue-100' : 'text-white';
  const values = showHistoricals ? row.historical : row.values;
  const subValues = showHistoricals ? row.historicalSub : row.subValues;
  return <>
      <tr className={`border-t border-white/10 ${tone}`} data-id="element-1031" data-mp="3cktn5kybjp3">
        <th scope="row" className={`px-2 ${isKey ? 'py-1.5' : 'py-1'} text-left text-[11px] font-bold text-white`} data-id="element-1032" data-mp="3f70f300pv69">
          {row.emphasis === 'consumables' && <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-blue-300 align-middle" aria-hidden="true" data-id="element-1033" data-mp="2a1mip2id2wr6" />}
          {row.label}
        </th>
        {values.map((value, index) => <td key={`${row.id}-${years[index]}`} className={`px-2 ${isKey ? 'py-1.5 text-xs' : 'py-1 text-[11px]'} text-right font-bold tabular-nums ${valueTone}`} data-id="element-1034" data-mp="20aruhslk1n33" data-mpi={index}>
            {value}
          </td>)}
      </tr>

      {row.sub && subValues && <tr className={tone} data-id="element-1035" data-mp="1mb8zm1hr888q">
          <th scope="row" className="px-2 pb-1 text-left text-[9px] font-semibold italic text-slate-400" data-id="element-1036" data-mp="1maz8ubovxx1z">{row.sub}</th>
          {subValues.map((value, index) => <td key={`${row.id}-sub-${years[index]}`} className="px-2 pb-1 text-right text-[9px] font-semibold italic tabular-nums text-slate-300" data-id="element-1037" data-mp="1ce4abssm3wnp" data-mpi={index}>
              {value}
            </td>)}
        </tr>}
    </>;
}